import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminRefreshFamousHoldersButton — Round P129 (Feb 2026).
 *
 * One-click shortcut for the famous-filer 13-F merge. Wraps
 * POST /api/admin/13f/merge-famous-filers, which pulls each famous
 * institution's holdings via their SEC CIK and merges them into
 * `thirteen_f_by_ticker`. Runs as a BackgroundTask on the server so
 * the request returns immediately.
 *
 * This is redundant with clicking "Run" on the weekly 13-F bucketing
 * cron (which now auto-chains the famous merge at the end) — but
 * the bucketing walk takes ~2 min, whereas this button takes ~50 s
 * total. Handy when the user only wants smart-money data refreshed
 * without rebucketing every ticker.
 */
export default function AdminRefreshFamousHoldersButton() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  // Poll for job status while running.
  useEffect(() => {
    if (!busy) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/admin/13f/merge-famous-filers/status`, {
          withCredentials: true,
        });
        if (cancelled) return;
        setStatus(r.data);
        if (!r.data?.in_progress) {
          setBusy(false);
          const s = r.data?.last_summary;
          if (s) {
            toast.success(
              `Famous holders refreshed — ${s.filers_processed} filers, ${s.tickers_updated} tickers updated in ${s.elapsed_seconds}s`,
              { duration: 6000 },
            );
          }
        }
      } catch (e) {
        // Ignore transient poll errors; keep polling until success or busy=false.
      }
    };
    const id = setInterval(poll, 3000);
    poll();
    return () => { cancelled = true; clearInterval(id); };
  }, [busy]);

  if (user?.role !== 'admin') return null;

  const onClick = async (includeLarge = false) => {
    if (busy) return;
    setBusy(true);
    setStatus(null);
    try {
      await axios.post(
        `${API}/admin/13f/merge-famous-filers?include_large=${includeLarge ? 'true' : 'false'}`,
        {},
        { withCredentials: true },
      );
      toast.info(
        includeLarge
          ? 'Full famous + large merge queued — expect ~3 minutes…'
          : 'Famous holders refresh queued — expect ~50 seconds…',
      );
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to queue job');
    }
  };

  const inProgress = busy || status?.in_progress;
  const lastCompleted = status?.last_completed_at;

  return (
    <div className="inline-flex items-center gap-1" data-testid="admin-refresh-famous-holders-wrap">
      <button
        type="button"
        onClick={() => onClick(false)}
        disabled={inProgress}
        data-testid="admin-refresh-famous-holders"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-l-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 text-[11px] font-semibold hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-60 transition-colors"
        title={lastCompleted ? `Famous only (13 filers, ~50 s) · Last run: ${new Date(lastCompleted).toLocaleString()}` : 'Refresh Smart-Money Crossover from 13 famous filers (~50 s)'}
      >
        {inProgress ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <Crown className="w-3 h-3" />
        )}
        {inProgress ? 'Refreshing…' : 'Famous (50 s)'}
      </button>
      <button
        type="button"
        onClick={() => onClick(true)}
        disabled={inProgress}
        data-testid="admin-refresh-famous-holders-full"
        className="inline-flex items-center gap-1 px-2 py-1 rounded-r-md bg-amber-100 dark:bg-amber-900/40 border border-l-0 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 text-[11px] font-semibold hover:bg-amber-200 dark:hover:bg-amber-800/60 disabled:opacity-60 transition-colors"
        title="Famous + top-50 index funds (~3 min) — pads mid-cap bucket depth"
      >
        + Large (3 min)
      </button>
    </div>
  );
}
